import { Box, BoxSx, DropdownOption } from "@modernatx/ui-kit-react-v2";

import { FinderHeaderBlockProps } from "@/finder/FinderHeader";
import { ClientGeolocationErrorMessages } from "@/finder/utils";
import { Finder } from "@/finder/V2/Finder";
import { ExitModal as ExitModalType, FinderMapBlockProps, Metadata } from "@/types/Block";
import { BlockTextProps } from "@/types/V2/BlockText";

import { ExitModal } from "../V2/blocks/ExitModal";

export interface AgePopupProps {
  bodyText: string;
  buttonText: string;
  headerText: string;
  inputLabel: string;
}
export interface BlockConfiguration {
  backgroundColor?: BoxSx["backgroundColor"];
  backgroundImage?: BoxSx["backgroundImage"];
  backgroundPosition?: BoxSx["backgroundPosition"];
  backgroundRepeat?: BoxSx["backgroundRepeat"];
  backgroundSize?: BoxSx["backgroundSize"];
  id?: string;
  key?: string;
  maxWidth?: BoxSx["maxWidth"];
  minHeight?: BoxSx["minHeight"];
  shadow?: BoxSx["boxShadow"];
  spacingBottom?: BoxSx["paddingBottom"];
  spacingTop?: BoxSx["paddingTop"];
  themeMode?: "light" | "dark";
  transition?: boolean;
  height?: BoxSx["height"];
  display?: BoxSx["display"];
  flexDirection?: BoxSx["flexDirection"];
  flexGrow?: BoxSx["flexGrow"];
  justifyContent?: BoxSx["justifyContent"];
}

export interface FinderBlockProps {
  distanceTemplate: string;
  finderListOptions?: {
    availabilityText?: {
      singular: string;
      plural: string;
    };
    listTitle?: string;
    scheduleNowText?: string;
  };
  loadMoreTemplate?: {
    singular: string;
    plural: string;
  };
  noResultsText?: string;
  searchThisAreaText?: string;
  header: FinderHeaderBlockProps;
  searchType: "branded" | "unbranded";
  search: {
    searchInputLabel: string;
    ageInputLabel: string;
    typeDropdownLabel: { selected: string; unselected: string };
    typeDropdownOptions: DropdownOption[];
    eligibility: {
      [key: string]: {
        default: string[];
        products: { [key: string]: string[] };
        priority?: string;
      };
    };
    clientGeolocationErrorMessages: ClientGeolocationErrorMessages;
    detectLocationDropdownLabel: string;
    agePopupContent: AgePopupProps;
  };
  map: FinderMapBlockProps;
}

export interface ExitModalBlockProps {
  text: BlockTextProps;
  buttonText: BlockTextProps["text"];
}

export type BlockProps =
  | {
      component: "Finder";
      block?: BlockConfiguration;
      props: FinderBlockProps;
    }
  | {
      component: "ExitModal";
      block?: BlockConfiguration;
      props: ExitModalBlockProps;
    };

const BlockComponent: React.FC<BlockProps> = (props) => {
  switch (props.component) {
    case "Finder":
      const { props: finderProps } = props;
      return <Finder {...finderProps} />;
    case "ExitModal":
      const { props: exitModalProps } = props;
      return <ExitModal {...exitModalProps} />;
  }
};

export const Block: React.FC<BlockProps> = (props) => {
  const { block } = props;

  if (block?.transition) {
    return (
      <Box
        id={block?.id}
        sx={{
          boxShadow: block?.shadow,
          minHeight: block?.minHeight,
          pb: block?.spacingBottom,
          pt: block?.spacingTop
        }}
      >
        <BlockComponent {...props} />
      </Box>
    );
  }

  return (
    <Box
      id={block?.id}
      sx={{
        backgroundColor: block?.backgroundColor || "surface-brand",
        backgroundImage: block?.backgroundImage,
        backgroundPosition: block?.backgroundPosition,
        backgroundRepeat: block?.backgroundRepeat,
        backgroundSize: block?.backgroundSize,
        boxShadow: block?.shadow,
        minHeight: block?.minHeight,
        pb: block?.spacingBottom,
        pt: block?.spacingTop,
        height: block?.height,
        flexDirection: block?.flexDirection,
        display: block?.display,
        flexGrow: block?.flexGrow
      }}
    >
      <BlockComponent {...props} />
    </Box>
  );
};

export interface BlockPageProps {
  backgroundColor: BoxSx["backgroundColor"];
  blocks: BlockProps[];
  exitModal?: ExitModalType;
  metadata: Metadata;
  wordBreak?: BoxSx["wordBreak"];
}
